<template>
    <div class="photograph_detail_box">
        <div class="container">
            <div class="breadcrumb_box">
                <bread-crumb :breadcrumb="breadcrumb"></bread-crumb>
            </div>
            <div v-if="detailLoading" class="detail-state" role="status">
                <i class="el-icon-loading" aria-hidden="true"></i> 正在加载主题信息
            </div>
            <div v-else-if="!selectedTheme" class="detail-state" role="alert">
                <p>{{detailError || "未找到该拍摄主题，请返回主题列表重新选择。"}}</p>
                <el-button type="primary" @click="$router.push({ name: 'photograph' })">返回拍摄主题</el-button>
            </div>
            <photograph-detail-info
                v-else
                :service-item-id="selectedServiceItemId"
                :theme-styles="themeStyles"
                @style-select="selectStyle">
            </photograph-detail-info>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import photographyCatalog from "@/tool/photographyCatalog.cjs";
import photographyBooking from "@/tool/photographyBooking.cjs";

const { createPhotographyBreadcrumb } = photographyBooking;
const { PHOTOGRAPHY_CATALOG_STATUS } = photographyCatalog;

export default {
    computed: {
        ...mapState({
            serviceItem: (state) => state.photography.booking.serviceItem,
            bookingLoadStatus: (state) => state.photography.bookingLoadStatus,
            bookingLoadError: (state) => state.photography.bookingLoadError,
            themeTypes: (state) => state.photography.catalog.themeTypes,
            catalogStatus: (state) => state.photography.catalogStatus,
            themeDetail: (state) => state.photography.themeDetail,
            themeDetailStatus: (state) => state.photography.themeDetailStatus,
            themeDetailError: (state) => state.photography.themeDetailError,
        }),
        catalogStatuses() { return PHOTOGRAPHY_CATALOG_STATUS; },
        themeRouteKey() {
            const themeId = String(this.$route.query.themeId || "");
            return themeId
                ? `${String(this.$route.query.themeTypeId || "")}:${themeId}`
                : `service:${String(this.$route.query.serviceItemId || "")}`;
        },
        detailLoading() {
            const status = this.$route.query.themeId ? this.themeDetailStatus : this.bookingLoadStatus;
            return status === PHOTOGRAPHY_CATALOG_STATUS.LOADING || status === PHOTOGRAPHY_CATALOG_STATUS.IDLE;
        },
        detailError() {
            return this.$route.query.themeId ? this.themeDetailError : this.bookingLoadError;
        },
        selectedTheme() {
            if (this.themeDetail) return this.themeDetail;
            if (!this.$route.query.themeId && this.serviceItem) {
                return {
                    themeTypeId: "",
                    themeId: "",
                    serviceItems: [{ serviceItemId: this.serviceItem.serviceItemId, styleName: this.serviceItem.styleName }],
                };
            }
            return null;
        },
        themeStyles() {
            return this.selectedTheme
                ? this.selectedTheme.serviceItems.map((item) => ({ serviceItemId: item.serviceItemId, name: item.styleName }))
                : [];
        },
        selectedServiceItemId() {
            const requested = String(this.$route.query.serviceItemId || "");
            const selected = this.themeStyles.find((style) => style.serviceItemId === requested) || this.themeStyles[0];
            return selected ? selected.serviceItemId : "";
        },
        breadcrumb() {
            return createPhotographyBreadcrumb(this.serviceItem || {});
        },
    },
    created() {
        this.loadRouteTheme();
    },
    watch: {
        themeRouteKey() {
            this.loadRouteTheme();
        },
    },
    methods: {
        ...mapActions([
            "loadPhotographyCatalog",
            "loadPhotographyThemeDetail",
            "loadPhotographyBooking",
            "selectPhotographyThemeServiceItem",
        ]),
        async loadRouteTheme() {
            const themeId = String(this.$route.query.themeId || "");
            const serviceItemId = String(this.$route.query.serviceItemId || "");
            if (!themeId) {
                if (serviceItemId) this.loadPhotographyBooking(serviceItemId).catch(() => {});
                return;
            }
            if (!this.themeTypes.length) {
                try {
                    await this.loadPhotographyCatalog();
                } catch (error) {
                    return;
                }
            }
            let themeTypeId = String(this.$route.query.themeTypeId || "");
            if (!themeTypeId) {
                const parent = this.themeTypes.find((themeType) => themeType.themes.some((theme) => theme.id === themeId));
                if (!parent) return;
                this.$router.replace({
                    name: "photographDetail",
                    query: { ...this.$route.query, themeTypeId: parent.id },
                });
                return;
            }
            this.loadPhotographyThemeDetail({
                themeTypeId,
                themeId,
                serviceItemId,
            }).catch(() => {});
        },
        async selectStyle(serviceItemId) {
            const style = this.themeStyles.find((item) => item.serviceItemId === String(serviceItemId));
            if (!style || style.serviceItemId === String(this.$route.query.serviceItemId || "")) return;
            try {
                await this.selectPhotographyThemeServiceItem(style.serviceItemId);
            } catch (error) {
                return;
            }
            this.$router.replace({
                name: "photographDetail",
                query: {
                    themeTypeId: this.selectedTheme.themeTypeId,
                    themeId: this.selectedTheme.themeId,
                    serviceItemId: style.serviceItemId,
                },
            });
        },
    },
    components: {
        breadCrumb: () => import("@/components/breadCrumb"),
        photographDetailInfo: () => import("@/components/photographDetailInfo"),
    },
};
</script>

<style scoped>
.photograph_detail_box{ min-height:100vh; overflow:hidden; background:#fff; }
.breadcrumb_box{ position:relative; z-index:1; margin:24px 0 18px; }
.detail-state{ min-height:360px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; color:#716c65; text-align:center; }
@media (max-width:767px){ .breadcrumb_box{ margin:16px 0 12px; } }
</style>
